import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Alert,
    Tooltip,
    Autocomplete,
    Stack,
    useMediaQuery,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import { AdminPageContainer, FormField } from '../components/admin/sharedStyles';
import PageHeader from '../components/PageHeader';
import { getInboundMail, resolveInboundMail } from '../redux/ducks/inboundMail';
import { getAccounts } from '../redux/ducks/account';
import { formatTimeAgo, formatDateTime } from '../utils/dateUtils';

const LoadingBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const EmptyState = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const SenderCell = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
});

const SubjectText = styled(Typography)({
    fontWeight: 600,
});

const SnippetText = styled(Typography)({
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
});

const HelperBlurb = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const NoticeAlert = styled(Alert)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const ActionStack = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
}));

const MobileList = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
}));

const ItemCard = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[3],
}));

const CardTop = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
}));

const Field = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    overflowWrap: 'anywhere',
});

const FieldLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size?.xs,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: theme.palette.text.secondary,
}));

const CaptionLine = styled(Typography)({
    overflowWrap: 'anywhere',
});

const REASON_TEXT = {
    unknown_sender: 'We could not match the sender to any contact on file.',
    ambiguous_account: 'The sender belongs to multiple accounts — pick which one this is for.',
    no_account: 'The sender is not linked to any account, so a project cannot be created automatically.',
    not_authorized: 'The sender is not allowed to post to this item.',
    bad_token: 'The reply link/token in the email was invalid or expired.',
    comments_disabled: 'Comments are turned off on the item this email is replying to.',
    parent_deleted: 'The item this email is replying to has been deleted.',
    parent_not_found: 'We could not find the item this email is replying to.',
    parent_privacy: 'The sender is not permitted to see the item they replied to.',
    cold_projects_disabled: 'Creating projects from unsolicited emails is turned off.',
    content_blocked: 'Blocked by the content filter.',
    parse_error: 'The email could not be parsed automatically.',
    auto_submitted: 'This looks machine-generated (an out-of-office reply, bounce, or mailing list), so nothing was created from it.',
    loop_detected: 'This email came from our own mailbox — held back to stop a mail loop.',
    sender_rate_limited: 'This sender has already had several projects created this hour, so this one was held for review.',
};

const CLASSIFICATION_LABEL = {
    reply: 'Reply',
    newProject: 'New project',
    unmatched: 'Unmatched',
    auto: 'Automated',
};

const STATUS_FILTERS = [
    { value: 'parked', label: 'Parked' },
    { value: 'failed', label: 'Failed' },
    { value: 'processed', label: 'Processed' },
];

const humanizeReason = (reason) => REASON_TEXT[reason] || (reason ? reason.replace(/_/g, ' ') : 'Needs review.');

const senderName = (item) => {
    if (item.resolvedUser?.fullName) return item.resolvedUser.fullName;
    if (item.resolvedUser?.first_name) return `${item.resolvedUser.first_name} ${item.resolvedUser.last_name || ''}`.trim();
    return item.from || 'Unknown sender';
};

const isReplyType = (item) => Boolean(item.parentType && item.parentId);

const InboundMail = () => {
    const dispatch = useDispatch();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const [status, setStatus] = useState('parked');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);

    const [active, setActive] = useState(null);
    const [saving, setSaving] = useState(false);
    const [dismissingId, setDismissingId] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [projectName, setProjectName] = useState('');
    const [overview, setOverview] = useState('');
    const [commentContent, setCommentContent] = useState('');

    const loadItems = useCallback(() => {
        setLoading(true);
        setErrored(false);
        dispatch(getInboundMail(status, (data) => {
            setLoading(false);
            if (!data) {
                setErrored(true);
                return;
            }
            setItems(Array.isArray(data) ? data : []);
        }));
    }, [dispatch, status]);

    useEffect(() => {
        loadItems();
    }, [loadItems]);

    const loadAccounts = useCallback(() => {
        setAccountsLoading(true);
        dispatch(getAccounts(0, 0, null, false, (data) => {
            setAccounts(Array.isArray(data) ? data : []);
            setAccountsLoading(false);
        }));
    }, [dispatch]);

    const openResolve = (item) => {
        setActive(item);
        setProjectName(item.subject || '');
        setOverview(item.body || item.textSnippet || '');
        setCommentContent(item.body || item.textSnippet || '');
        if (!isReplyType(item)) {
            setSelectedAccount(item.resolvedAccount?._id ? item.resolvedAccount : null);
            loadAccounts();
        }
    };

    const closeResolve = () => {
        if (saving) return;
        setActive(null);
    };

    const dismissItem = (item) => {
        setDismissingId(item._id);
        dispatch(resolveInboundMail(item._id, { action: 'dismiss' }, (data) => {
            setDismissingId(null);
            if (data) loadItems();
        }));
    };

    const submit = (body) => {
        if (!active) return;
        setSaving(true);
        dispatch(resolveInboundMail(active._id, body, (data) => {
            setSaving(false);
            if (!data) return; // error/409 — alert already shown, keep dialog open to retry
            setActive(null);
            loadItems();
        }));
    };

    const reply = active && isReplyType(active);
    const replyHasNoSender = reply && !active?.resolvedUser;
    const commentsDisabled = active?.reason === 'comments_disabled';
    const accountMissing = !selectedAccount;

    return (
        <AdminPageContainer>
            <PageHeader
                title="Inbound Email Triage"
                subtitle="Client emails that could not be handled automatically. Review each one and decide what to do with it."
                actions={
                    <Tooltip title="Reload the list">
                        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadItems}>
                            Refresh
                        </Button>
                    </Tooltip>
                }
                filters={
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={status}
                        onChange={(e, value) => value && setStatus(value)}
                        aria-label="Filter inbound email by status"
                    >
                        {STATUS_FILTERS.map((f) => (
                            <ToggleButton key={f.value} value={f.value}>
                                {f.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                }
            />

            {loading ? (
                <LoadingBox>
                    <CircularProgress />
                </LoadingBox>
            ) : errored ? (
                <EmptyState>
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                        We could not load the triage inbox.
                    </Typography>
                    <Button variant="outlined" onClick={loadItems}>Try again</Button>
                </EmptyState>
            ) : items.length === 0 ? (
                <EmptyState>
                    <Typography variant="body1" color="text.secondary">
                        {status === 'processed' ? 'No processed emails yet.' : `Nothing here. No ${STATUS_FILTERS.find((f) => f.value === status)?.label.toLowerCase()} emails to review.`}
                    </Typography>
                </EmptyState>
            ) : isMobile ? (
                <MobileList>
                    {items.map((item) => (
                        <ItemCard key={item._id}>
                            <CardTop>
                                <Typography variant="body2" fontWeight="bold">{senderName(item)}</Typography>
                                <Chip label={CLASSIFICATION_LABEL[item.classification] || item.classification || '—'} size="small" variant="outlined" />
                            </CardTop>
                            {item.resolvedUser && item.from && (
                                <CaptionLine variant="caption" color="text.secondary">{item.from}</CaptionLine>
                            )}
                            {item.resolvedAccount?.name && (
                                <Typography variant="caption" color="text.secondary">{item.resolvedAccount.name}</Typography>
                            )}
                            <Field>
                                <SubjectText variant="body2">{item.subject || '(no subject)'}</SubjectText>
                                {item.textSnippet && (
                                    <Typography variant="caption" color="text.secondary">{item.textSnippet}</Typography>
                                )}
                            </Field>
                            <Field>
                                <FieldLabel>Why it needs review</FieldLabel>
                                <Typography variant="caption">{humanizeReason(item.reason)}</Typography>
                            </Field>
                            <Field>
                                <FieldLabel>Received</FieldLabel>
                                <Tooltip title={formatDateTime(item.receivedAt)}>
                                    <Typography variant="caption">{item.receivedAt ? formatTimeAgo(item.receivedAt) : '—'}</Typography>
                                </Tooltip>
                            </Field>
                            {status === 'processed' ? (
                                <Typography variant="caption" color="text.secondary">Done</Typography>
                            ) : (
                                <ActionStack>
                                    <Button fullWidth variant="contained" onClick={() => openResolve(item)}>
                                        Resolve
                                    </Button>
                                    <Tooltip title="Clear this from the queue without taking any action">
                                        <span>
                                            <Button
                                                fullWidth
                                                color="error"
                                                disabled={dismissingId === item._id}
                                                onClick={() => dismissItem(item)}
                                            >
                                                {dismissingId === item._id ? 'Dismissing…' : 'Dismiss'}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </ActionStack>
                            )}
                        </ItemCard>
                    ))}
                </MobileList>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>From</strong></TableCell>
                                <TableCell><strong>Subject</strong></TableCell>
                                <TableCell><strong>Type</strong></TableCell>
                                <TableCell><strong>Why it needs review</strong></TableCell>
                                <TableCell><strong>Received</strong></TableCell>
                                <TableCell align="right"><strong>Action</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item) => (
                                <TableRow key={item._id} hover>
                                    <TableCell>
                                        <SenderCell>
                                            <Typography variant="body2" fontWeight="bold">{senderName(item)}</Typography>
                                            {item.resolvedUser && item.from && (
                                                <Typography variant="caption" color="text.secondary">{item.from}</Typography>
                                            )}
                                            {item.resolvedAccount?.name && (
                                                <Typography variant="caption" color="text.secondary">{item.resolvedAccount.name}</Typography>
                                            )}
                                        </SenderCell>
                                    </TableCell>
                                    <TableCell>
                                        <SubjectText variant="body2">{item.subject || '(no subject)'}</SubjectText>
                                        {item.textSnippet && (
                                            <SnippetText variant="caption" color="text.secondary">{item.textSnippet}</SnippetText>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={CLASSIFICATION_LABEL[item.classification] || item.classification || '—'} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">{humanizeReason(item.reason)}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={formatDateTime(item.receivedAt)}>
                                            <Typography variant="caption">{item.receivedAt ? formatTimeAgo(item.receivedAt) : '—'}</Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="right">
                                        {status === 'processed' ? (
                                            <Typography variant="caption" color="text.secondary">Done</Typography>
                                        ) : (
                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                <Button size="small" variant="contained" onClick={() => openResolve(item)}>
                                                    Resolve
                                                </Button>
                                                <Tooltip title="Clear this from the queue without taking any action">
                                                    <span>
                                                        <Button
                                                            size="small"
                                                            color="error"
                                                            disabled={dismissingId === item._id}
                                                            onClick={() => dismissItem(item)}
                                                        >
                                                            {dismissingId === item._id ? 'Dismissing…' : 'Dismiss'}
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                            </Stack>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={!!active} onClose={closeResolve} maxWidth="sm" fullWidth>
                <DialogTitle>Resolve email</DialogTitle>
                <DialogContent>
                    <HelperBlurb variant="body2" color="text.secondary">
                        {active && humanizeReason(active.reason)}
                    </HelperBlurb>
                    {active && (
                        <NoticeAlert severity="info" icon={false}>
                            <Typography variant="caption" display="block"><strong>From:</strong> {senderName(active)}{active.from && active.resolvedUser ? ` (${active.from})` : ''}</Typography>
                            <Typography variant="caption" display="block"><strong>Subject:</strong> {active.subject || '(no subject)'}</Typography>
                        </NoticeAlert>
                    )}

                    {reply ? (
                        <ActionStack>
                            <Typography variant="body2" color="text.secondary">
                                This email is a reply. You can post its content as a comment on the original item, attributed to the sender.
                            </Typography>
                            {replyHasNoSender && (
                                <NoticeAlert severity="warning" icon={false}>
                                    <Typography variant="caption">
                                        No matching user for this sender — we can&apos;t attribute a comment. Dismiss this email, or have the sender added as a user first, then retry.
                                    </Typography>
                                </NoticeAlert>
                            )}
                            <FormField>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    label="Comment to post"
                                    value={commentContent}
                                    onChange={(e) => setCommentContent(e.target.value)}
                                    placeholder={active.body || active.textSnippet || ''}
                                    helperText="Pre-filled with the original email. Edit it before posting if needed."
                                    disabled={replyHasNoSender}
                                />
                            </FormField>
                            <Button
                                variant="contained"
                                disabled={saving || replyHasNoSender}
                                onClick={() => submit({ action: 'post-comment', content: commentContent.trim() || undefined })}
                            >
                                Post as comment
                            </Button>
                            {commentsDisabled && (
                                <Button
                                    variant="outlined"
                                    disabled={saving || replyHasNoSender}
                                    onClick={() => submit({ action: 'enable-comments-and-post', content: commentContent.trim() || undefined })}
                                >
                                    Enable comments &amp; post
                                </Button>
                            )}
                        </ActionStack>
                    ) : (
                        <ActionStack>
                            <Typography variant="body2" color="text.secondary">
                                Turn this email into a new project for the sender's account.
                            </Typography>
                            <FormField>
                                <Autocomplete
                                    options={accounts}
                                    loading={accountsLoading}
                                    getOptionLabel={(o) => o.name || ''}
                                    value={selectedAccount}
                                    onChange={(_, v) => setSelectedAccount(v)}
                                    isOptionEqualToValue={(o, v) => o._id === v._id}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Account"
                                            required
                                            placeholder="Select the account for this project"
                                            helperText="The project will be created under this account."
                                        />
                                    )}
                                    fullWidth
                                />
                            </FormField>
                            <FormField>
                                <TextField
                                    fullWidth
                                    label="Project name"
                                    value={projectName}
                                    onChange={(e) => setProjectName(e.target.value)}
                                />
                            </FormField>
                            <FormField>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    label="Overview"
                                    value={overview}
                                    onChange={(e) => setOverview(e.target.value)}
                                />
                            </FormField>
                            <Button
                                variant="contained"
                                disabled={saving || accountMissing}
                                onClick={() =>
                                    submit({
                                        action: 'create-project',
                                        account: selectedAccount?._id,
                                        name: projectName.trim() || undefined,
                                        overview: overview.trim() || undefined,
                                    })
                                }
                            >
                                Create project
                            </Button>
                            {accountMissing && (
                                <Typography variant="caption" color="error">
                                    Pick an account before creating the project.
                                </Typography>
                            )}
                        </ActionStack>
                    )}

                    <ActionStack>
                        <Typography variant="body2" color="text.secondary">
                            If this email needs no action, dismiss it to clear it from the inbox.
                        </Typography>
                        <Button color="error" disabled={saving} onClick={() => submit({ action: 'dismiss' })}>
                            Dismiss email
                        </Button>
                    </ActionStack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeResolve} disabled={saving}>Close</Button>
                    {saving && <CircularProgress size={20} />}
                </DialogActions>
            </Dialog>
        </AdminPageContainer>
    );
};

export default InboundMail;
